import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';

interface OptionGroup {
  label: string;
  options: any[];
}

type FlatItem =
  | { type: 'group'; label: string }
  | { type: 'option'; option: any; optionIndex: number };

let nextSelectId = 0;

@Component({
  selector: 'prism-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SelectComponent implements OnInit, OnChanges {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('listboxEl') listboxEl?: ElementRef<HTMLDivElement>;
  @ViewChild('triggerEl') triggerEl?: ElementRef<HTMLDivElement>;

  dropdownTop = 0;
  dropdownLeft = 0;
  dropdownWidth = 0;
  @Output() select = new EventEmitter();
  @Input() control!: FormControl | any;
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() selectOptions: any[] = [];
  @Input() placeHolder: string = 'Select an option';
  @Input() hasSearch: boolean = false;
  @Input() readOnly?: boolean = false;
  @Input() labelClass: string = '';
  @Input() allowDeselect: boolean = false;
  @Input() ariaSelector: string = '';
  @Input() ariaLabel: string = '';
  @Input() initialId: number = 1;
  @Input() multiSelect: boolean = false;

  optionGroups: OptionGroup[] = [];
  isOpen = false;
  searchText = '';
  activeIndex = -1;

  private readonly uid = `prism-select-${++nextSelectId}`;
  readonly inputId = `${this.uid}-input`;
  readonly listboxId = `${this.uid}-listbox`;
  readonly labelId = `${this.uid}-label`;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.initOptionGroups();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.initOptionGroups();
  }

  get accessibleName(): string {
    return this.label?.trim() || this.ariaLabel?.trim() || this.placeHolder;
  }

  get effectiveLabel(): string {
    return this.label?.trim() || this.placeHolder?.trim() || '';
  }

  get effectiveLabelClass(): string {
    return this.label?.trim() ? this.labelClass : 'sr-only';
  }

  get filteredGroups(): OptionGroup[] {
    if (!this.searchText) return this.optionGroups;
    const needle = this.searchText.toLowerCase();
    return this.optionGroups
      .map((g) => ({
        label: g.label,
        options: g.options.filter((o) =>
          (o?.name ?? '').toString().toLowerCase().includes(needle)
        ),
      }))
      .filter((g) => g.options.length > 0);
  }

  get flatOptions(): any[] {
    const all: any[] = [];
    for (const g of this.filteredGroups) {
      for (const o of g.options) all.push(o);
    }
    return all;
  }

  get flatItems(): FlatItem[] {
    const items: FlatItem[] = [];
    let optionIndex = 0;
    const groups = this.filteredGroups;
    const showGroupLabels = groups.length > 1;
    for (const g of groups) {
      if (showGroupLabels && g.label) {
        items.push({ type: 'group', label: g.label });
      }
      for (const o of g.options) {
        items.push({ type: 'option', option: o, optionIndex: optionIndex++ });
      }
    }
    return items;
  }

  get inputDisplayValue(): string {
    if (this.isOpen) return this.searchText;
    if (this.multiSelect) return '';
    const selected = this.findOption(this.control?.value);
    return selected?.name ?? '';
  }

  get selectedChips(): any[] {
    if (!this.multiSelect) return [];
    const ids = this.toArray(this.control?.value);
    return ids
      .map((id: any) => this.findOption(id))
      .filter((o: any) => o != null);
  }

  optionId(option: any): string {
    return `${this.uid}-opt-${option?.id}`;
  }

  get activeOptionId(): string | null {
    if (!this.isOpen) return null;
    const opt = this.flatOptions[this.activeIndex];
    return opt ? this.optionId(opt) : null;
  }

  private scrollActiveIntoView(): void {
    const id = this.activeOptionId;
    if (!id) return;
    const el = this.listboxEl?.nativeElement?.querySelector(`#${CSS.escape(id)}`);
    el?.scrollIntoView({ block: 'nearest' });
  }

  isSelected(option: any): boolean {
    if (option == null) {
      const v = this.control?.value;
      return v == null || (Array.isArray(v) && v.length === 0);
    }
    if (this.multiSelect) {
      return this.toArray(this.control?.value).includes(option.id);
    }
    return this.control?.value === option.id;
  }

  isDisabledOption(option: any): boolean {
    const selected = this.toArray(this.control?.value);
    return (
      this.multiSelect &&
      !selected.includes(option.id) &&
      selected.length >= 20
    );
  }

  convertToArray(value: any): any[] {
    return this.toArray(value);
  }

  searchOptions(searchText: string): void {
    this.searchText = searchText ?? '';
  }

  private toArray(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (value != null && value !== '') return [value];
    return [];
  }

  private findOption(id: any): any {
    if (id == null) return null;
    for (const g of this.optionGroups) {
      const found = g.options.find((o) => o?.id === id);
      if (found) return found;
    }
    return null;
  }

  private initOptionGroups(): void {
    this.optionGroups = [
      { label: 'All Options', options: this.selectOptions ?? [] },
    ];
  }

  open(): void {
    if (this.readOnly || this.isOpen) return;
    this.positionDropdown();
    this.isOpen = true;
    this.activeIndex = this.initialActiveIndex();
  }

  private positionDropdown(): void {
    const trigger = this.triggerEl?.nativeElement;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    this.dropdownTop = rect.bottom + 4;
    this.dropdownLeft = rect.left;
    this.dropdownWidth = rect.width;
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.searchText = '';
    this.activeIndex = -1;
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  private initialActiveIndex(): number {
    const opts = this.flatOptions;
    if (!this.multiSelect && this.control?.value != null) {
      const idx = opts.findIndex((o) => o?.id === this.control.value);
      if (idx >= 0) return idx;
    }
    return opts.length > 0 ? 0 : -1;
  }

  onTriggerClick(event: MouseEvent): void {
    if (this.readOnly) return;
    if (!this.isOpen) {
      this.open();
    }
    this.focusInput();
  }

  onInputChange(value: string): void {
    this.searchText = value;
    if (!this.isOpen) this.open();
    const opts = this.flatOptions;
    this.activeIndex = opts.length > 0 ? 0 : -1;
  }

  selectOption(option: any): void {
    if (option === null) {
      this.updateValue(this.multiSelect ? [] : null);
      this.select.emit(null);
      this.close();
      return;
    }
    if (this.isDisabledOption(option)) return;

    if (this.multiSelect) {
      const current = this.toArray(this.control?.value);
      const updated = current.includes(option.id)
        ? current.filter((id: any) => id !== option.id)
        : [...current, option.id];
      this.updateValue(updated);
      this.select.emit(updated);
      this.searchText = '';
      this.focusInput();
    } else {
      this.updateValue(option.id);
      this.select.emit(option.id);
      this.close();
    }
  }

  removeChip(option: any, event: Event): void {
    event.stopPropagation();
    if (this.readOnly) return;
    const current = this.toArray(this.control?.value);
    const updated = current.filter((id: any) => id !== option.id);
    this.updateValue(updated);
    this.select.emit(updated);
  }

  private updateValue(value: any): void {
    this.control?.setValue(value);
    this.control?.markAsDirty();
    this.control?.markAsTouched();
  }

  onKeyDown(event: KeyboardEvent): void {
    const opts = this.flatOptions;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else if (opts.length) {
          this.activeIndex = (this.activeIndex + 1) % opts.length;
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else if (opts.length) {
          this.activeIndex =
            (this.activeIndex - 1 + opts.length) % opts.length;
        }
        break;
      case 'Home':
        if (this.isOpen && opts.length) {
          event.preventDefault();
          this.activeIndex = 0;
        }
        break;
      case 'End':
        if (this.isOpen && opts.length) {
          event.preventDefault();
          this.activeIndex = opts.length - 1;
        }
        break;
      case 'Enter':
        if (
          this.isOpen &&
          this.activeIndex >= 0 &&
          this.activeIndex < opts.length
        ) {
          event.preventDefault();
          this.selectOption(opts[this.activeIndex]);
        } else if (!this.isOpen) {
          event.preventDefault();
          this.open();
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.close();
        }
        break;
      case 'Tab':
        if (this.isOpen) {
          this.close();
        }
        break;
      case 'Backspace':
        if (this.multiSelect && !this.searchText) {
          const current = this.toArray(this.control?.value);
          if (current.length) {
            const updated = current.slice(0, -1);
            this.updateValue(updated);
            this.select.emit(updated);
          }
        }
        break;
    }
    this.scrollActiveIntoView();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.close();
    }
  }

  private focusInput(): void {
    queueMicrotask(() => this.searchInput?.nativeElement?.focus());
  }
}
